import { useState } from 'react'
import { router } from '@inertiajs/react'
import { AlertCircle, CheckCircle2, Shield, ShieldCheck, ShieldAlert, ChevronDown, ChevronRight } from 'lucide-react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { StageBadge } from '@/components/shared/StageBadge'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { PaymentSummary } from '@/components/shared/PaymentSummary'
import { DocumentSection } from '@/components/shared/DocumentSection'
import { CalendarEventsSection } from '@/components/shared/CalendarEventsSection'
import { DetailGrid } from '@/components/backoffice/DetailGrid'
import { formatDate } from '@/lib/utils'
import { BackofficeLicenseDetailed, BackofficeLicenseRecordDetailed } from '@/types'

type Props = {
  license: BackofficeLicenseDetailed
}


export default function LicensesShow({ license }: Props) {
  const frequencyLabel = license.frequency ? license.frequency.replace('_', '-') : 'One-time'
  const [expandedRecords, setExpandedRecords] = useState<Record<string, boolean>>({})

  const toggleRecord = (id: string) => {
    setExpandedRecords(prev => ({ ...prev, [id]: !prev[id] }))
  }

  return (
    <BackofficeLayout title={license.display_name} breadcrumbs={[{ label: 'Backoffice', href: '/backoffice/dashboard' }, { label: 'Licenses', href: '/backoffice/licenses' }, { label: license.display_name }]}>
      <div className="space-y-6">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">{license.display_name}</h1>
            <StatusBadge status={license.status} />
            <Badge variant="outline" className="font-normal text-xs">{frequencyLabel}</Badge>
            {license.issued && <Badge variant="outline" className="font-normal text-xs"><ShieldCheck className="w-3 h-3 mr-1 inline" />Issued</Badge>}
            {license.expired && <Badge className="bg-red-100 text-red-700 font-normal text-xs"><ShieldAlert className="w-3 h-3 mr-1 inline" />Expired</Badge>}
            {license.custom && <Badge variant="outline" className="font-normal text-xs"><Shield className="w-3 h-3 mr-1 inline" />Custom</Badge>}
            {license.perpetual && <Badge variant="outline" className="font-normal text-xs">Perpetual</Badge>}
          </div>
          <p className="text-sm text-muted-foreground">{license.business_name} · {license.account_name} · {license.jurisdiction_label || license.type_code}</p>
        </div>

        {license.issuance && (
          <div className="border rounded-lg px-4 py-3 bg-green-50">
            <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Issuance</p>
            <div className="flex gap-6 text-sm">
              <span>Issued: {new Date(license.issuance.issued_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              {license.issuance.expires_on ? (
                <span className={license.expired ? 'text-red-600 font-medium' : ''}>
                  Expires: {new Date(license.issuance.expires_on).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              ) : (
                <span className="text-green-600">Perpetual — no expiry</span>
              )}
            </div>
          </div>
        )}

        {license.custom_request && (
          <div className="border rounded-lg px-4 py-3 bg-blue-50">
            <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Custom Request</p>
            <p className="text-sm font-medium">{license.custom_request.name}</p>
            {license.custom_request.description && (
              <p className="text-sm text-muted-foreground mt-0.5">{license.custom_request.description}</p>
            )}
          </div>
        )}

        <DetailGrid items={[
          { label: 'Start Date', value: new Date(license.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) },
          {
            label: 'Next Due Date',
            value: license.next_due_date
              ? <span className={license.next_due_date && new Date(license.next_due_date) < new Date() ? 'text-red-600 font-medium' : ''}>
                  {new Date(license.next_due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              : '—',
          },
          { label: 'Frequency', value: frequencyLabel },
          { label: 'Jurisdiction', value: license.jurisdiction_label || license.type_code || '—' },
          { label: 'Status', value: <StatusBadge status={license.status} /> },
        ]} />

        <div>
          <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-2">License Records</p>
          {license.records.length === 0 ? (
            <p className="text-sm text-muted-foreground">No records yet</p>
          ) : (
            <div className="space-y-3">
              {license.records.map((record) => (
                <RecordDetail
                  key={record.id}
                  record={record}
                  licenseId={license.id}
                  expanded={!!expandedRecords[record.id]}
                  onToggle={() => toggleRecord(record.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </BackofficeLayout>
  )
}

function RecordDetail({ record, licenseId, expanded, onToggle }: {
  record: BackofficeLicenseRecordDetailed
  licenseId: string
  expanded: boolean
  onToggle: () => void
}) {
  const confirm = useConfirm()
  const [showRejectForm, setShowRejectForm] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  const [refNumber, setRefNumber] = useState('')

  const handleFilingSubmit = () => {
    router.post(`/backoffice/licenses/${licenseId}/records/${record.id}/filing`, { reference_number: refNumber })
  }

  const handleAccept = async () => {
    if (!await confirm({ title: 'Accept Filing?', description: 'This will mark the filing as accepted.' })) return
    router.post(`/backoffice/licenses/${licenseId}/records/${record.id}/filing/acceptance`)
  }

  const handleRejectConfirm = () => {
    if (!rejectionReason.trim()) return
    router.post(`/backoffice/licenses/${licenseId}/records/${record.id}/filing/rejection`, { reason: rejectionReason.trim() })
    setShowRejectForm(false)
    setRejectionReason('')
  }

  const handleComplete = async () => {
    if (!await confirm({ title: 'Mark as Complete?', description: 'Mark this license period as complete?' })) return
    router.post(`/backoffice/licenses/${licenseId}/records/${record.id}/completion`)
  }

  const basePath = `/backoffice/licenses/${licenseId}/records/${record.id}`

  return (
    <div className="border rounded-lg">
      {/* Record header — click to expand/collapse */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          <span className="text-sm font-medium">
            {formatDate(record.period_start_date)} — {formatDate(record.period_end_date)}
          </span>
          <StageBadge stage={record.stage} variant="license" />
          {record.payment?.paid && (
            <Badge className="bg-green-600 text-white font-normal text-xs"><CheckCircle2 className="w-3 h-3 mr-1" />Paid</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Due: {formatDate(record.due_date)}</span>
          {record.overdue && (
            <Badge className="bg-red-600 text-white font-normal text-xs flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />Overdue
            </Badge>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t px-4 py-4 space-y-4">
          {/* Payment */}
          <PaymentSummary
            payment={record.payment ? {
              id: record.payment.id,
              service_fee_cents: record.payment.service_fee_cents,
              gov_fee_cents: record.payment.gov_fee_cents,
              total_cents: record.payment.total_cents,
              currency: record.payment.currency,
              paid: record.payment.paid,
              paid_at: record.payment.paid_at,
              invoice_number: record.payment.invoice_number,
              receipt_number: record.payment.receipt_number,
              refunded: record.payment.refunded,
              refund_amount_cents: record.payment.refund_amount_cents,
            } : null}
            adminActions={record.payment ? {
              confirmPaymentPath: !record.payment.paid ? `${basePath}/payments/${record.payment.id}/payment_confirmation` : undefined,
              invoicePath: record.payment.invoice ? `${basePath}/payments/${record.payment.id}/invoice` : undefined,
              refundPath: record.payment.paid && !record.payment.refunded ? `${basePath}/payments/${record.payment.id}/refund/new` : undefined,
            } : undefined}
            collapsible={false}
          />

          {/* Filing Submission */}
          {!record.filing && record.payment?.paid && (
            <section className="border rounded-lg px-4 py-3">
              <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-2">Submit Filing</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Reference number (optional)"
                  value={refNumber}
                  onChange={e => setRefNumber(e.target.value)}
                  className="flex-1 h-8 text-sm border rounded-md px-3 focus:outline-none focus:ring-1 focus:ring-ring"
                />
                <Button size="sm" className="h-8 text-xs" onClick={handleFilingSubmit}>Submit Filing</Button>
              </div>
            </section>
          )}

          {/* Filing Status */}
          {record.filing && (
            <section className="border rounded-lg px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Filing Status</p>
                <div className="flex items-center gap-2">
                  {record.filing.accepted && <Badge className="bg-green-600 text-white font-normal text-xs">Accepted</Badge>}
                  {record.filing.rejected && <Badge className="bg-red-600 text-white font-normal text-xs">Rejected</Badge>}
                  {record.filing.pending && <Badge className="bg-blue-600 text-white font-normal text-xs">Pending</Badge>}
                </div>
              </div>
              <div className="space-y-1.5">
                {record.filing.submitted_at && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Submitted</span>
                    <span>{formatDate(record.filing.submitted_at)}</span>
                  </div>
                )}
                {record.filing.reference_number && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Reference</span>
                    <span>{record.filing.reference_number}</span>
                  </div>
                )}
                {record.filing.rejection_reason && (
                  <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                    {record.filing.rejection_reason}
                  </div>
                )}
              </div>
              {!record.filing.accepted && !record.filing.rejected && record.payment?.paid && (
                <div className="mt-3 pt-2 border-t space-y-3">
                  {!showRejectForm ? (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handleAccept} className="h-7 text-xs">Accept</Button>
                      <Button variant="outline" size="sm" onClick={() => setShowRejectForm(true)} className="h-7 text-xs text-red-600">Reject</Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Rejection Reason</p>
                      <textarea
                        className="w-full border rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-ring"
                        rows={3}
                        placeholder="Enter rejection reason..."
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                      />
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={handleRejectConfirm} disabled={!rejectionReason.trim()} className="h-7 text-xs text-red-600">
                          Confirm Rejection
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setShowRejectForm(false); setRejectionReason('') }} className="h-7 text-xs">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Completion */}
          {!record.completion && record.filing?.accepted && (
            <section className="border rounded-lg px-4 py-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Mark as Complete</p>
                <Button size="sm" onClick={handleComplete} className="h-7 text-xs">Complete</Button>
              </div>
            </section>
          )}

          {record.completion && (
            <section className="border rounded-lg px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-600">Completed</span>
                </div>
                <span className="text-xs text-muted-foreground">{formatDate(record.completion.created_at)}</span>
              </div>
            </section>
          )}

          {/* Documents */}
          <DocumentSection
            categories={[
              {
                label: 'Application Documents',
                documents: record.application_documents,
                uploadPath: `${basePath}/application_documents`,
                deletePath: `${basePath}/application_documents`,
              },
              {
                label: 'Filing Documents',
                documents: record.filing_documents,
                uploadPath: `${basePath}/filing_documents`,
                deletePath: `${basePath}/filing_documents`,
              },
              {
                label: 'Completion Documents',
                documents: record.completion_documents,
                uploadPath: `${basePath}/completion_documents`,
                deletePath: `${basePath}/completion_documents`,
              },
            ]}
          />

          {/* Calendar Events */}
          <CalendarEventsSection
            events={record.calendar_events}
            collapsible={false}
          />
        </div>
      )}
    </div>
  )
}
